import React, { useEffect, useMemo, useState } from 'react';
import { useQuery } from 'react-query';
import { Helmet } from 'react-helmet-async';
import { useTranslation } from 'react-i18next';
import dynamicAPI from '../../api/dynamicAPI';
import '../../css/frontend/boardgames-view.css';

const url = '/api/view/boardgames';

function getPlayersLabel(bg) {
    const min = bg?.minPlayers;
    const max = bg?.maxPlayers;

    if (min && max) {
        if (min === max) return `${min}`;
        return `${min}–${max}`;
    }
    if (min && !max) return `${min}+`;
    return '—';
}

export default function BoardgamesView() {
    const { t } = useTranslation();
    const [q, setQ] = useState('');
    const [page, setPage] = useState(1);
    const limit = 48;

    const { data, isLoading, isError, error, refetch, isFetching } = useQuery(
        ['view-boardgames', page, limit, q],
        async () => await dynamicAPI('get', `${url}?page=${page}&limit=${limit}&q=${encodeURIComponent(q)}`, {}),
        { retry: 0, keepPreviousData: true }
    );
    const [activeImg, setActiveImg] = useState(null);
    const openImage = (src, alt) => setActiveImg({ src, alt });
    const closeImage = () => setActiveImg(null);
    useEffect(() => {
        const timer = setTimeout(() => {
            setPage(1);
            refetch();
        }, 250);
        return () => clearTimeout(timer);
    }, [q]); // eslint-disable-line

    // close on Escape + lock scroll
    useEffect(() => {
        if (!activeImg) return;

        const onKeyDown = (e) => {
            if (e.key === 'Escape') closeImage();
        };

        document.addEventListener('keydown', onKeyDown);
        const prevOverflow = document.body.style.overflow;
        document.body.style.overflow = 'hidden';

        return () => {
            document.removeEventListener('keydown', onKeyDown);
            document.body.style.overflow = prevOverflow;
        };
    }, [activeImg]);

    const items = useMemo(() => data?.data ?? [], [data]);

    return (
        <>
            <Helmet>
                <title>{t('SpaceTime Boardgames List')}</title>
                <meta name="description" content="Boardgame library" />
            </Helmet>

            <div className="bgv-page">
                <div className="bgv-header">
                    <div className="bgv-titleRow">
                        <h2 className="bgv-title">{t('SpaceTime Boardgames List')}</h2>
                        <div className="bgv-count">[{data?.total ?? '—'}]</div>
                    </div>

                    <div className="bgv-searchRow">
                        <div className="bgv-searchWrap">
                            <span className="bgv-searchIcon">🔎</span>
                            <input
                                className="bgv-searchInput"
                                value={q}
                                onChange={(e) => setQ(e.target.value)}
                                placeholder={t('Search by title, notes, tags…')}
                            />
                        </div>

                        <button
                            type="button"
                            className="bgv-searchBtn"
                            onClick={() => refetch()}
                            disabled={isFetching}
                            title={t('Refresh')}
                        >
                            {isFetching ? <span className="bgv-spinner" /> : null}
                            {isFetching ? t('Searching…') : t('Search')}
                        </button>
                    </div>
                </div>

                {isLoading ? (
                    <div className="bgv-state">
                        <span className="bgv-spinner bgv-spinner-lg" />
                        <div className="bgv-stateText">{t('Loading…')}</div>
                    </div>
                ) : isError ? (
                    <div className="bgv-state bgv-stateError">
                        <div className="bgv-stateTitle">😵 {t('Something went wrong')}</div>
                        <div className="bgv-stateText">{error?.message || 'Failed to load'}</div>
                        <button className="bgv-ghostBtn" onClick={() => refetch()}>
                            {t('Try again')}
                        </button>
                    </div>
                ) : (
                    <div className="bgv-list">
                        {items.length ? (
                            items.map((bg) => {
                                const imgPath =
                                    bg.images?.length
                                        ? bg.images.find((x) => x.featured)?.path || bg.images[0]?.path
                                        : 'item-no-image.png';

                                const owned = bg.ownership !== 'lent';

                                return (
                                    <article key={bg._id} className="bgv-card">
                                        <div className="bgv-thumb">
                                            <button
                                                type="button"
                                                className="bgv-thumbBtn"
                                                onClick={() => openImage(`${data.images_path}${imgPath}`, bg.title)}
                                                aria-label={t('Open image')}
                                            >
                                                <img
                                                    className="bgv-thumbImg"
                                                    src={`${data.images_path}${imgPath}`}
                                                    alt={bg.title}
                                                    loading="lazy"
                                                />
                                            </button>
                                        </div>

                                        <div className="bgv-main">
                                            <div className="bgv-topRow">
                                                <div className="bgv-name">{bg.title}</div>

                                                <div className="bgv-metaRow">
                                                    <div className="bgv-pill bgv-pillPlayers" title={t('Players')}>
                                                        <span className="bgv-pillIcon">👥</span>
                                                        <span className="bgv-pillLabel">{t('Players')}:</span>
                                                        <strong className="bgv-pillValue">{getPlayersLabel(bg)}</strong>
                                                    </div>

                                                    <div className={`bgv-pill ${owned ? 'bgv-pillOwned' : 'bgv-pillLent'}`}>
                                                        <span className="bgv-pillIcon">{owned ? '🏠' : '🤝'}</span>
                                                        <strong className="bgv-pillValue">{owned ? t('Owned') : t('Lent')}</strong>
                                                    </div>
                                                </div>
                                            </div>

                                            <div className="bgv-tags">
                                                {Array.isArray(bg.tags) && bg.tags.length ? (
                                                    bg.tags.map((tag) => (
                                                        <span key={tag} className="bgv-chip">
                              #{tag}
                            </span>
                                                    ))
                                                ) : (
                                                    <span className="bgv-chip bgv-chipMuted">{t('No tags')}</span>
                                                )}
                                            </div>

                                            {bg.notes ? <div className="bgv-notes">{bg.notes}</div> : null}
                                        </div>

                                        <div className="bgv-actions">
                                            {bg.link ? (
                                                <a
                                                    className="bgv-rulesBtn"
                                                    href={bg.link}
                                                    target="_blank"
                                                    rel="noreferrer"
                                                    title={t('Open rules')}
                                                >
                                                    <span className="bgv-rulesBtnIcon">📜</span>
                                                    <span className="bgv-rulesBtnText">{t('Rules')}</span>
                                                </a>
                                            ) : (
                                                <button className="bgv-rulesBtn bgv-rulesBtnDisabled" disabled>
                                                    <span className="bgv-rulesBtnIcon">❌</span>
                                                    <span className="bgv-rulesBtnText">{t('No rules')}</span>
                                                </button>
                                            )}
                                        </div>
                                    </article>
                                );
                            })
                        ) : (
                            <div className="bgv-empty">
                                <div className="bgv-emptyEmoji">🎲</div>
                                <div className="bgv-emptyTitle">{t('No boardgames found')}</div>
                                <div className="bgv-emptySub">{t('Try a different search')}</div>
                            </div>
                        )}
                    </div>
                )}

                {data?.pages > 1 && (
                    <div className="bgv-pagination">
                        <button className="bgv-pageBtn" disabled={page <= 1} onClick={() => setPage((p) => p - 1)}>
                            ← {t('Prev')}
                        </button>

                        <div className="bgv-pageIndicator">
                            {t('Page')} <strong>{page}</strong> / {data.pages}
                        </div>

                        <button className="bgv-pageBtn" disabled={page >= data.pages} onClick={() => setPage((p) => p + 1)}>
                            {t('Next')} →
                        </button>
                    </div>
                )}
                {activeImg && (
                    <div
                        className="bgv-modalOverlay"
                        role="dialog"
                        aria-modal="true"
                        aria-label={t('Image preview')}
                        onPointerDown={(e) => {
                            // only close when clicking the overlay, not the content
                            if (e.target === e.currentTarget) closeImage();
                        }}
                    >
                        <div className="bgv-modalContent" onPointerDown={(e) => e.stopPropagation()}>
                            <button
                                type="button"
                                className="bgv-modalClose"
                                onClick={closeImage}
                                aria-label={t('Close')}
                            >
                                ×
                            </button>

                            <img className="bgv-modalImg" src={activeImg.src} alt={activeImg.alt} />
                        </div>
                    </div>
                )}

            </div>
        </>
    );
}
